/**
 * The Spotify Web Playback SDK, as a small typed surface — phase 19d.
 *
 * # Why this is a service and not a hook
 *
 * A page may mount and unmount while a game runs; a *player* must not. The SDK
 * registers a device with Spotify and holds a websocket, and creating a second
 * one steals playback from the first. So there is exactly one, owned here.
 *
 * # 🔴 The sink is one device, and it is the host's phone
 *
 * Phase 19 decision 3: the host's phone is the audio sink in **both** device
 * layouts. That is not a limitation to work around — it is what removes the
 * packaged-TV OAuth problem entirely (ADR 0005: `location.origin` is the string
 * `"null"` there, so a redirect flow has nowhere to come back to). The
 * television never authenticates with Spotify because it never plays Spotify.
 *
 * # The 30 seconds is a seek and a stop, not a clip
 *
 * A local clip is a pre-cut 30-second file. A Spotify track is the whole song,
 * so the window is `seek → play → stop on a timer` — and it must not start at
 * 0:00, because a guessing game whose every round opens with an intro is a
 * different, worse game. See {@link startOffsetMs}.
 */

import { serverUrl } from "./serverOrigin.js";

/** Where the SDK itself comes from. Spotify serves it and requires it be theirs. */
const SDK_SRC = "https://sdk.scdn.co/spotify-player.js";

/** What went wrong, in terms a screen can say out loud. */
export type PlayerFault =
  /** Not a Premium account. The SDK cannot play at all — this is not retryable. */
  | "not_premium"
  /** No connected Spotify account, or the installation has none configured. */
  | "no_account"
  /** Reached Spotify and it refused, or the SDK failed to initialise. */
  | "unavailable";

interface SpotifyPlayerLike {
  connect(): Promise<boolean>;
  disconnect(): void;
  addListener(event: string, cb: (payload: unknown) => void): boolean;
}

declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady?: () => void;
    Spotify?: {
      Player: new (options: {
        name: string;
        getOAuthToken: (cb: (token: string) => void) => void;
        volume?: number;
      }) => SpotifyPlayerLike;
    };
  }
}

/** Fetch an access token for the sink. */
async function accessToken(): Promise<string> {
  const response = await fetch(serverUrl("/api/spotify/token"), {
    credentials: "same-origin",
  });
  if (!response.ok) {
    throw new Error("no_account");
  }
  const body = (await response.json()) as { access_token: string };
  return body.access_token;
}

let sdkLoading: Promise<void> | null = null;

/**
 * Load the SDK script once.
 *
 * The SDK announces itself by calling a global, so the promise resolves on that
 * rather than on the script's `load` event — a loaded script that has not
 * finished initialising has no `window.Spotify` yet.
 */
function loadSdk(): Promise<void> {
  if (window.Spotify) {
    return Promise.resolve();
  }
  sdkLoading ??= new Promise<void>((resolve, reject) => {
    window.onSpotifyWebPlaybackSDKReady = () => {
      resolve();
    };
    const script = document.createElement("script");
    script.src = SDK_SRC;
    script.async = true;
    script.onerror = () => {
      reject(new Error("unavailable"));
    };
    document.head.append(script);
  });
  return sdkLoading;
}

/** One live player, or none. */
interface Live {
  player: SpotifyPlayerLike;
  deviceId: string;
}

let live: Live | null = null;
let starting: Promise<Live> | null = null;

/**
 * Get the one player, creating it if needed.
 *
 * @throws Error whose message is a {@link PlayerFault}.
 */
export async function sink(): Promise<Live> {
  if (live) {
    return live;
  }
  starting ??= start();
  try {
    return await starting;
  } catch (cause) {
    // A failed start must not poison every later attempt: somebody who was
    // offline when the round began should be able to try the next one.
    starting = null;
    throw cause;
  }
}

async function start(): Promise<Live> {
  await loadSdk();
  const Spotify = window.Spotify;
  if (!Spotify) {
    throw new Error("unavailable");
  }

  return new Promise<Live>((resolve, reject) => {
    const player = new Spotify.Player({
      name: "sonolume",
      getOAuthToken: (cb) => {
        // Called again whenever the token expires, which is why the SDK wants a
        // callback rather than a string: an hour is shorter than some parties.
        void accessToken().then(cb, () => {
          reject(new Error("no_account"));
        });
      },
      volume: 0.8,
    });

    player.addListener("ready", (payload) => {
      const { device_id: deviceId } = payload as { device_id: string };
      live = { player, deviceId };
      resolve(live);
    });
    // 🔴 The one fault worth naming separately: the Web Playback SDK is a
    // Premium-only feature. A free account produces this and nothing else will
    // ever work, so a screen must say so rather than offering "try again".
    player.addListener("account_error", () => {
      reject(new Error("not_premium"));
    });
    player.addListener("initialization_error", () => {
      reject(new Error("unavailable"));
    });
    player.addListener("authentication_error", () => {
      reject(new Error("no_account"));
    });

    void player.connect().then((ok) => {
      if (!ok) {
        reject(new Error("unavailable"));
      }
    });
  });
}

/**
 * Where in a track a 30-second window should start.
 *
 * **Not zero**, and that is a game-design decision rather than a technical one:
 * a local clip is pre-cut to the interesting part, so a Spotify round that
 * always opened on the intro would be measurably harder in a way that has
 * nothing to do with knowing the song.
 *
 * 30% in, clamped so the window never runs past the end. Deterministic, so a
 * track sounds the same every time it is dealt — which matters because two
 * parties comparing notes is a thing that happens.
 *
 * Exported for its test: this is arithmetic worth pinning.
 */
export function startOffsetMs(durationMs: number, windowSeconds: number): number {
  const windowMs = windowSeconds * 1000;
  if (!Number.isFinite(durationMs) || durationMs <= windowMs) {
    return 0;
  }
  const preferred = Math.floor(durationMs * 0.3);
  return Math.max(0, Math.min(preferred, durationMs - windowMs));
}

/** How long a track is, so the window can be placed inside it. */
async function durationMs(trackId: string, token: string): Promise<number> {
  const response = await fetch(`https://api.spotify.com/v1/tracks/${trackId}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    return 0;
  }
  const body = (await response.json()) as { duration_ms?: number };
  return body.duration_ms ?? 0;
}

/**
 * Play a track on the sink for `seconds`, then pause.
 *
 * Playback is started through the **Web API** rather than the SDK: the SDK
 * exposes transport controls (pause, seek, volume) but no "play this uri", so
 * the documented route is `PUT /me/player/play` aimed at the SDK's device id.
 *
 * @throws Error whose message is a {@link PlayerFault}.
 */
export async function playWindow(trackId: string, seconds: number): Promise<() => void> {
  const { deviceId } = await sink();
  const token = await accessToken();
  const position = startOffsetMs(await durationMs(trackId, token), seconds);

  const response = await fetch(
    `https://api.spotify.com/v1/me/player/play?device_id=${encodeURIComponent(deviceId)}`,
    {
      method: "PUT",
      headers: {
        authorization: `Bearer ${token}`,
        "content-type": "application/json",
      },
      body: JSON.stringify({
        uris: [`spotify:track:${trackId}`],
        position_ms: position,
      }),
    },
  );
  if (!response.ok) {
    throw new Error(response.status === 403 ? "not_premium" : "unavailable");
  }

  // The stop half of "seek and stop". Returned as a canceller rather than left
  // to fire blindly, because a round can end early — a void, a disconnect, or
  // simply everybody answering — and audio that outlives its round is the most
  // obvious kind of broken.
  const stop = window.setTimeout(() => {
    void pause(token);
  }, seconds * 1000);
  return () => {
    window.clearTimeout(stop);
    void pause(token);
  };
}

async function pause(token: string): Promise<void> {
  await fetch("https://api.spotify.com/v1/me/player/pause", {
    method: "PUT",
    headers: { authorization: `Bearer ${token}` },
  }).catch(() => {
    // A pause that fails is not worth surfacing: the window has closed either
    // way, and the next cue replaces whatever is playing.
  });
}
